import { useContext } from "@nuxtjs/composition-api";
import { useFavourites } from "./useFavourites";
import { FAVOURITE_ENTITY_TYPES } from "./favouriteEntityTypes";
import { useFetchProfessionals } from "~/components/_refactored/profession";
import ADD_FAVOURITE from "~/graphql/mutations/addFavourite.graphql";

const SYNCED_FLAG = "favourites_synced_v1";
const LEGACY_LIKED_KEY = "likedPersons";

/**
 * One-time, best-effort migration of the legacy localStorage-based professional
 * favourites onto the backend Favourites API. Runs once per browser (guarded by
 * a localStorage flag) after the user logs in.
 *
 * Each legacy liked person id is pushed via addFavourite (idempotent server
 * side). On completion the legacy localStorage state is cleared and the
 * in-memory favourites cache is reloaded.
 */
export function useSyncLocalFavourites() {
  const context = useContext();
  const { $mutate, $logger, $helpers, localePath, store } = context;
  const fetchProfessionals = useFetchProfessionals(context);
  const { loadFavourites } = useFavourites();

  function alreadySynced() {
    try {
      return window.localStorage.getItem(SYNCED_FLAG) === "1";
    } catch (e) {
      return false;
    }
  }

  function markSynced() {
    try {
      window.localStorage.setItem(SYNCED_FLAG, "1");
    } catch (e) {
      // ignore storage errors
    }
  }

  function clearLegacy() {
    try {
      store.commit("localStorage/setLikedPersonIds", []);
      window.localStorage.removeItem(LEGACY_LIKED_KEY);
    } catch (e) {
      // ignore storage errors
    }
  }

  function buildPersonUrl(seoName) {
    return `${$helpers.topDomain}${localePath({
      name: "professionalPage",
      params: { professionalSeoName: seoName },
    })}`;
  }

  return async function syncLocalFavourites() {
    if (process.server || alreadySynced()) {
      return;
    }

    const likedPersonIds = store.getters["localStorage/likedPersonIds"] ?? [];
    if (!likedPersonIds.length) {
      markSynced();
      return;
    }

    try {
      const { people = [] } = await fetchProfessionals({
        personIds: likedPersonIds,
        pageSize: likedPersonIds.length,
      });

      await Promise.all(
        people.map((person) =>
          $mutate(ADD_FAVOURITE, {
            input: {
              entityType: FAVOURITE_ENTITY_TYPES.PROFESSIONAL,
              entityId: String(person.id),
              url: buildPersonUrl(person.seoName),
            },
          }).catch((e) => {
            $logger.warn("Failed to sync legacy favourite", {
              personId: person.id,
              error: e,
            });
          })
        )
      );

      markSynced();
      clearLegacy();
      await loadFavourites();
    } catch (e) {
      // Best-effort: never block login on a failed sync. Leave the flag unset
      // so a later login can retry.
      $logger.warn("Legacy favourites sync failed", e);
    }
  };
}
